import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageCircle, Send } from 'lucide-react';
import * as Icons from 'lucide-react';
import { heroSlides, stats, services, whyChoose, features, process, lifecycle, industries, testimonials, software, faqs } from '../mock/mockData';
import DotConnect from '../components/DotConnect';
import GlobalReachSection from '../components/GlobalReachSection';
import RotatingStatsBar from '../components/RotatingStatsBar';
import HeroLineArt from '../components/HeroLineArt';
import FeaturedIndustriesCarousel from '../components/FeaturedIndustriesCarousel';
import useSeo from '../hooks/useSeo';
import { useLang } from '../contexts/LanguageContext';

const Typewriter = ({ words, className = '', onIndexChange }) => {
  const { tt } = useLang();
  const [index, setIndex] = useState(0);
  const [sub, setSub] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const localized = words.map(w => tt(w));

  useEffect(() => {
    const current = localized[index];
    let t;
    if (!deleting && sub < current.length) {
      t = setTimeout(() => setSub(sub + 1), 110);
    } else if (!deleting && sub === current.length) {
      t = setTimeout(() => setDeleting(true), 1400);
    } else if (deleting && sub > 0) {
      t = setTimeout(() => setSub(sub - 1), 55);
    } else {
      setDeleting(false);
      const next = (index + 1) % localized.length;
      setIndex(next);
      if (onIndexChange) onIndexChange(next);
    }
    return () => clearTimeout(t);
  }, [sub, deleting, index, localized, onIndexChange]);

  return <span className={className}>{localized[index].slice(0, sub)}<span className="inline-block w-[3px] h-[0.9em] bg-orange-500 ml-1 animate-pulse align-middle"></span></span>;
};

// (Hero image slider replaced by HeroLineArt — animated CAD line art per typewriter word.)

const Hero = () => {
  const h = heroSlides[0];
  const { t, tt } = useLang();
  const [wordIdx, setWordIdx] = useState(0);
  return (
    <section className="relative overflow-hidden bg-[#0e1116] text-white">
      {/* Animated dot-connect network */}
      <DotConnect density={90} color="#94a3b8" lineColor="rgba(148,163,184,0.85)" accentColor="#f97316" maxDistance={150} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(148,163,184,0.18),transparent_55%)] pointer-events-none"></div>
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>

      <div className="relative max-w-[1400px] mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-gray-900/50 border border-gray-700/50 text-green-300 text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-2 rounded mb-6">
            <Icons.Globe2 className="w-3.5 h-3.5" /> {tt('Global Engineering Services')}
          </div>
          <h1 className="text-5xl md:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tight">
            {tt(h.title)}{' '}
            <span className="text-green-500">{tt(h.highlight1)}</span>{' '}
            {tt(h.middle)}{' '}
            <span className="text-orange-500">{tt(h.highlight2)}</span>{' '}
            <Typewriter words={h.typewriter} className="text-white" onIndexChange={setWordIdx} />
          </h1>
          <p className="text-gray-400 mt-7 text-lg max-w-xl leading-relaxed">{tt(h.description)}</p>
          <div className="flex flex-wrap gap-4 mt-9">
            <Link to="/get-quote" className="btn-shine group inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-4 rounded uppercase text-sm tracking-wider transition-all hover:shadow-xl hover:shadow-orange-500/40">
              {t('getQuote')} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 border border-gray-600 hover:border-orange-500 hover:text-orange-400 text-white font-bold px-7 py-4 rounded uppercase text-sm tracking-wider transition-all">
              <Icons.Layers className="w-4 h-4" /> {t('ourServices')}
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-gray-600/20 to-orange-500/10 rounded-lg blur-2xl"></div>
          <div className="relative">
            <HeroLineArt word={h.typewriter[wordIdx] || h.typewriter[0]} accent="#94a3b8" accentAlt="#f97316" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white text-[#0e1116] px-6 py-4 rounded shadow-xl flex items-center gap-3 z-20">
            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center text-white">
              <Icons.CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <div className="font-black text-2xl leading-none">15K+</div>
              <div className="text-[11px] uppercase tracking-wider text-gray-600">{tt('Projects Delivered')}</div>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 bg-orange-500 text-white px-4 py-3 rounded shadow-xl flex items-center gap-2 z-20">
            <Icons.Globe2 className="w-5 h-5" />
            <div>
              <div className="font-black text-lg leading-none">40+</div>
              <div className="text-[10px] uppercase tracking-wider opacity-90">{tt('Countries')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* stats bar — auto-rotates through services */}
      <RotatingStatsBar />
    </section>
  );
};

const SectionLabel = ({ children }) => {
  const { tt } = useLang();
  return (
    <div className="text-green-500 font-bold text-[12px] uppercase tracking-[0.25em] mb-3">
      {typeof children === 'string' ? tt(children) : children}
    </div>
  );
};

const SectionTitle = ({ children, type, highlight }) => (
  <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight">{children}</h2>
);

const About = () => {
  const { tt } = useLang();
  return (
  <section className="py-24 bg-white">
    <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <SectionLabel>About Us</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-black leading-tight text-[#0e1116]">
          {tt('Trusted')} <span className="text-green-600">{tt('Engineering')}</span><br />
          <Typewriter words={['Experts', 'Partners', 'Consultants', 'Innovators']} className="text-orange-500" />
        </h2>
        <p className="text-gray-600 mt-6 leading-relaxed">
          {tt('Land Drafters is a globally integrated, multi-disciplinary engineering and CAD drafting consultancy with operations across the United States and India. We operate across the complete project lifecycle — from feasibility and conceptual design through construction documentation and project management.')}
        </p>
        <p className="text-gray-600 mt-4 leading-relaxed">
          {tt('Our unique combination of global engineering standards, advanced digital methodologies, and globally distributed execution ensures technically robust, cost-efficient, and scalable solutions for every project, regardless of scale or geography.')}
        </p>

        <div className="grid grid-cols-2 gap-5 mt-8">
          <Highlight icon="Award" title="Expertise" desc="Built on accuracy, efficiency, and cutting-edge technology." />
          <Highlight icon="Compass" title="Mission" desc="Delivering cost-effective, reliable, customized solutions." />
          <Highlight icon="Globe" title="Global Reach" desc="Projects delivered across 5 continents with 350+ clients." />
          <Highlight icon="ShieldCheck" title="ISO 9001:2015" desc="Certified quality management with 3-stage QC process." />
        </div>

        <Link to="/about" className="inline-flex items-center gap-2 mt-8 bg-[#0e1116] hover:bg-gray-700 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider transition-colors">
          {tt('Learn More')} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="relative">
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-gray-500/10 rounded-full"></div>
        <div className="absolute -bottom-4 -left-4 w-40 h-40 bg-orange-500/10 rounded-full"></div>
        <img src="https://images.unsplash.com/photo-1628155858736-bacadae33a88?w=1200&h=900&fit=crop" alt="Engineering" className="relative w-full h-[540px] object-cover rounded-lg" />
        <div className="absolute top-6 left-6 right-6 bottom-6 border-2 border-white/20 rounded pointer-events-none"></div>
      </div>
    </div>
  </section>
  );
};

const Highlight = ({ icon, title, desc }) => {
  const Ic = Icons[icon] || Icons.Check;
  const { tt } = useLang();
  return (
    <div className="flex gap-3">
      <div className="w-11 h-11 shrink-0 rounded bg-gray-100 text-green-700 flex items-center justify-center">
        <Ic className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-bold text-[#0e1116]">{tt(title)}</h4>
        <p className="text-xs text-gray-500 mt-1">{tt(desc)}</p>
      </div>
    </div>
  );
};

const Services = () => {
  const { tt } = useLang();
  return (
    <section id="services" className="py-24 bg-[#0e1116] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px]"></div>
      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <SectionLabel>Our Services</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            {tt('BROWSE')} <Typewriter words={['SERVICES', 'EXPERTISE', 'CAPABILITIES']} className="text-orange-500" />
          </h2>
          <p className="text-gray-400 mt-5">{tt('Fast, simple, and convenient engineering & CAD design services tailored to your project requirements across every discipline.')}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 12).map((s, i) => {
            const Ic = Icons[s.icon] || Icons.Box;
            const accent = i % 2 === 0 ? 'bg-gray-600' : 'bg-orange-500';
            return (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group relative block bg-[#1a1f2e] border border-gray-900/40 rounded-lg overflow-hidden hover:border-green-500 transition-all hover:-translate-y-1 duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-110 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116] via-[#0e1116]/60 to-transparent"></div>
                </div>
                <div className="relative px-6 pb-6 -mt-7">
                  <div className={`w-12 h-12 ${accent} flex items-center justify-center rounded shadow-xl`}><Ic className="w-6 h-6 text-white" /></div>
                  <h3 className="font-bold text-xl mt-4 group-hover:text-green-400 transition-colors">{tt(s.title)}</h3>
                  <p className="text-gray-400 text-sm mt-2 leading-relaxed">{tt(s.short)}</p>
                  <div className="flex items-center gap-2 mt-4 text-green-500 text-xs font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                    {tt('Explore')} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Link to="/services" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-7 py-4 rounded uppercase text-sm tracking-wider transition-all hover:shadow-xl hover:shadow-orange-500/30">
            {tt('View All Services')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const WhyChoose = () => {
  const { tt } = useLang();
  return (
  <section className="py-24 bg-gray-50">
    <div className="max-w-[1400px] mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <SectionLabel>Why Choose Us</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-black leading-tight text-[#0e1116]">{tt('The')} <span className="text-green-600">{tt('Advantage')}</span> {tt('of Partnership')}</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {whyChoose.map((w) => {
          const Ic = Icons[w.icon] || Icons.Check;
          return (
            <div key={w.title} className="group bg-white border border-gray-200 rounded-lg p-7 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-green-300">
              <div className="w-14 h-14 rounded bg-gradient-to-br from-gray-600 to-gray-500 text-white flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Ic className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-lg text-[#0e1116]">{tt(w.title)}</h3>
              <p className="text-gray-500 text-sm mt-2 leading-relaxed">{tt(w.desc)}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-14 text-center">
        <SectionLabel>Key Features</SectionLabel>
        <h3 className="text-3xl font-black mb-10 text-[#0e1116]">{tt('Capabilities That Set Us')} <span className="text-orange-500">{tt('Apart')}</span></h3>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f) => {
          const Ic = Icons[f.icon] || Icons.Star;
          return (
            <div key={f.title} className="flex gap-4 p-5 bg-white border border-gray-200 rounded hover:border-orange-400 hover:shadow-md transition-all">
              <div className="w-12 h-12 shrink-0 rounded bg-orange-100 text-orange-600 flex items-center justify-center">
                <Ic className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-bold text-[#0e1116]">{tt(f.title)}</h4>
                <p className="text-gray-500 text-sm mt-1">{tt(f.desc)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

const Process = () => {
  const { tt } = useLang();
  return (
  <section className="py-24 bg-[#0e1116] text-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:80px_80px]"></div>
    <div className="relative max-w-[1400px] mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <SectionLabel>How We Work</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-black">{tt('Predictable.')} <span className="text-green-500">{tt('Transparent.')}</span> <span className="text-orange-500">{tt('On-Schedule.')}</span></h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
        {process.map((p) => (
          <div key={p.num} className="relative bg-[#1a1f2e] border border-gray-900/40 rounded p-6 hover:border-green-500 transition-all group">
            <div className="text-6xl font-black text-orange-400 group-hover:text-orange-300 drop-shadow-lg leading-none transition-colors">{p.num}</div>
            <h3 className="font-bold text-lg mt-2 group-hover:text-orange-400 transition-colors">{tt(p.title)}</h3>
            <p className="text-gray-400 text-sm mt-2">{tt(p.desc)}</p>
          </div>
        ))}
      </div>

      <div className="text-center mt-20 max-w-2xl mx-auto mb-10">
        <SectionLabel>Project Lifecycle</SectionLabel>
        <h3 className="text-3xl font-black">{tt('From Concept to')} <span className="text-orange-500">{tt('Completion')}</span></h3>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {lifecycle.map((p) => (
          <div key={p.num} className="relative bg-gradient-to-b from-gray-900/30 to-transparent border border-gray-900/40 rounded p-5 hover:border-orange-500 transition-all">
            <div className="text-orange-500 font-black text-lg mb-2">{p.num}</div>
            <h4 className="font-bold text-sm">{tt(p.title)}</h4>
            <p className="text-gray-400 text-xs mt-2 leading-relaxed">{tt(p.desc)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const IndustriesSection = () => {
  const { tt } = useLang();
  return (
  <section className="py-24 bg-white">
    <div className="max-w-[1400px] mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <SectionLabel>Industries Served</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-black leading-tight text-[#0e1116]">{tt('Domain Depth')} <span className="text-green-600">{tt('Across Sectors')}</span></h2>
        <p className="text-gray-500 mt-4">{tt('Construction, infrastructure, real estate, environmental, manufacturing and more — we serve diverse industries with specialized expertise.')}</p>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
        {industries.map((ind) => {
          const Ic = Icons[ind.icon] || Icons.Briefcase;
          return (
            <div key={ind.title} className="group bg-gray-50 border border-gray-200 rounded p-5 text-center hover:bg-[#0e1116] hover:border-green-500 transition-all duration-300 cursor-pointer">
              <Ic className="w-9 h-9 mx-auto text-green-600 group-hover:text-orange-400 transition-colors" />
              <p className="text-xs font-bold mt-3 text-[#0e1116] group-hover:text-white transition-colors leading-tight">{tt(ind.title)}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

const Testimonials = () => {
  const { tt } = useLang();
  return (
  <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
    <div className="max-w-[1400px] mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <SectionLabel>Testimonials</SectionLabel>
        <h2 className="text-4xl md:text-5xl font-black text-[#0e1116]">{tt('What Our')} <span className="text-orange-500">{tt('Clients')}</span> {tt('Say')}</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {testimonials.slice(0, 8).map((t, i) => (
          <div key={`${t.name}-${t.location}-${i}`} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <Icons.Quote className="w-8 h-8 text-orange-400 mb-3" />
            <p className="text-gray-700 text-sm italic leading-relaxed">“{tt(t.quote)}”</p>
            <div className="mt-5 pt-4 border-t border-gray-100">
              <div className="font-bold text-[#0e1116] text-sm">{t.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">{tt(t.location)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

const SoftwareSection = () => {
  const { tt } = useLang();
  const cats = Object.keys(software);
  const [active, setActive] = useState(cats[0]);
  return (
    <section className="py-24 bg-[#0e1116] text-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <SectionLabel>Software We Use</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black">{tt('Industry-Standard')} <span className="text-green-500">{tt('Platforms')}</span></h2>
          <p className="text-gray-400 mt-4">{tt('From AutoCAD and Revit to ArcGIS and Tekla — our team works across 50+ professional platforms.')}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {cats.map(c => (
            <button key={c} onClick={() => setActive(c)} className={`px-5 py-2.5 rounded text-xs font-bold uppercase tracking-wider transition-all inline-flex items-center gap-2 ${active === c ? 'bg-gray-600 text-white shadow-lg shadow-gray-600/30' : 'bg-[#1a1f2e] border border-gray-900/50 text-gray-300 hover:border-green-500'}`}>
              <Icons.Folder className="w-3.5 h-3.5" /> {tt(c)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {software[active].map(s => (
            <div key={s.name} className="group bg-white rounded-lg p-5 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 hover:border-green-300">
              <div className="w-14 h-14 mx-auto bg-gray-50 rounded flex items-center justify-center mb-3 p-2 group-hover:scale-110 transition-transform">
                {s.iconUrl ? (
                  <img
                    src={s.iconUrl}
                    alt={s.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; const fb = e.target.nextElementSibling; if (fb) fb.style.display = 'flex'; }}
                  />
                ) : null}
                <span
                  className="w-full h-full rounded items-center justify-center font-black text-white text-sm"
                  style={{ background: `#${s.color}`, display: s.iconUrl ? 'none' : 'flex' }}
                >
                  {s.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                </span>
              </div>
              <p className="text-sm font-bold text-[#0e1116]">{s.name}</p>
              <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">{s.vendor}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const { tt } = useLang();
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="py-24 bg-gradient-to-b from-white via-gray-50/40 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <SectionLabel>FAQs</SectionLabel>
          <h2 className="text-4xl md:text-5xl font-black text-[#0e1116]">{tt('Frequently Asked')} <span className="text-green-600">{tt('Questions')}</span></h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">{tt('Quick answers on engagement, pricing, security and delivery.')} {tt("Don't see your question?")} <Link to="/contact" className="text-orange-500 font-bold hover:underline">{tt('Ask us directly →')}</Link></p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="faq-grid">
          {faqs.map((f, i) => (
            <div
              key={f.q}
              data-testid={`faq-item-${i}`}
              className={`border rounded-lg overflow-hidden transition-all bg-white ${openIdx === i ? 'border-gray-500 shadow-lg shadow-gray-500/10' : 'border-gray-200 hover:border-green-300'}`}
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-gray-50/40"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-bold text-[#0e1116] text-[15px] leading-snug">
                  <span className="text-orange-500 mr-2">Q{i + 1}.</span>{tt(f.q)}
                </span>
                <Icons.ChevronDown className={`w-5 h-5 text-green-600 shrink-0 mt-0.5 transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
              </button>
              {openIdx === i && (
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100">
                  <span className="text-green-600 font-bold mr-2">A.</span>{tt(f.a)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = () => {
  const { tt } = useLang();
  return (
  <section className="relative py-20 bg-gradient-to-br from-[#0e1116] via-gray-950 to-[#0e1116] text-white overflow-hidden">
    <div className="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_50%,rgba(148,163,184,0.4),transparent_50%),radial-gradient(circle_at_80%_30%,rgba(249,115,22,0.3),transparent_50%)]"></div>
    <div className="relative max-w-5xl mx-auto px-6 text-center">
      <h2 className="text-4xl md:text-6xl font-black leading-tight">
        {tt('Ready to Build Something')} <span className="text-orange-500">{tt('Great?')}</span>
      </h2>
      <p className="text-gray-300 mt-5 max-w-2xl mx-auto">{tt('From feasibility to final delivery — tell us about your project and let our global engineering team develop the precise solution you need.')}</p>
      <div className="flex flex-wrap justify-center gap-4 mt-9">
        <Link to="/get-quote" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded uppercase text-sm tracking-wider transition-all hover:shadow-2xl hover:shadow-orange-500/40">
          {tt('Get a Quote')} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-500 hover:border-orange-500 hover:text-orange-400 text-white font-bold px-8 py-4 rounded uppercase text-sm tracking-wider transition-all">
          {tt('Contact Us')}
        </Link>
      </div>
    </div>
  </section>
  );
};

const FloatingActions = () => (
  <div className="fixed left-4 bottom-6 z-40 flex flex-col gap-3">
    <a href="https://wa.me/919960250279" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110">
      <MessageCircle className="w-5 h-5 text-white" />
    </a>
    <a href="https://t.me/CADPartner" target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-sky-500 hover:bg-sky-400 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110">
      <Send className="w-5 h-5 text-white" />
    </a>
    <a href="tel:+17866732523" className="w-12 h-12 rounded-full bg-orange-500 hover:bg-orange-400 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110">
      <Phone className="w-5 h-5 text-white" />
    </a>
  </div>
);

const Home = () => {
  useSeo({
    title: 'Home',
    description: 'Land Drafters — global engineering and CAD drafting consultancy delivering surveying, BIM, structural and MEP services across 40+ countries.',
    path: '/'
  });
  return (
    <>
      <Hero />
      <FeaturedIndustriesCarousel />
      <About />
      <Services />
      <WhyChoose />
      <Process />
      <IndustriesSection />
      <GlobalReachSection />
      <Testimonials />
      <SoftwareSection />
      <FAQ />
      <CTA />
      <FloatingActions />
    </>
  );
};

export default Home;
export { Typewriter, SectionLabel, FloatingActions, CTA };
